/* Urgent-alert opt-in: what this device can do, and the calls that turn it on.
 *
 * The DECISIONS are pure functions (`alertState`, `describeTest`) so they can be
 * tested without a browser. The CALLS (`enableAlerts` and friends) are thin and
 * go through the real API; they need a real browser and are checked by hand with
 * the test button on each device - a headless browser has no push service.
 *
 * WHY THE STATES ARE WHAT THEY ARE (P1 push design, section 8.5):
 *  - iPhone only delivers web push to an app opened from the HOME SCREEN. A
 *    person who enables it in a Safari tab gets a permission prompt that goes
 *    nowhere, and "Allow" there does not carry over to the installed app. So
 *    not-installed on iOS is its own state, with instructions, and never offers
 *    the button.
 *  - Permission may only be requested from a tap. `enableAlerts` is called from
 *    a click handler and asks FIRST, before any await, so the browser still
 *    counts it as a user gesture.
 */

import { assertLiveWritesAllowed, guardedFetch } from './liveWrites.js';

const CSRF = { 'x-requested-by': 'ihs-it-help' };

/** Work out what to show. `env` is a plain object so a test can fake a device:
 *   { hasServiceWorker, hasPush, hasNotification, permission,
 *     isIos, isStandalone, subscribed } */
export function alertState(env) {
  if (!env.hasServiceWorker || !env.hasPush || !env.hasNotification) {
    // iOS Safari in a normal tab has no PushManager at all, so "unsupported" on
    // iOS usually means "not installed yet" - which is fixable, so say that.
    return env.isIos && !env.isStandalone ? 'needs-install' : 'unsupported';
  }
  if (env.isIos && !env.isStandalone) return 'needs-install';
  if (env.permission === 'denied') return 'blocked';
  if (env.permission === 'granted' && env.subscribed) return 'on';
  return 'off';
}

/** Read the real device. Kept apart from `alertState` so that stays testable. */
export async function readEnvironment() {
  const hasServiceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const hasPush = typeof window !== 'undefined' && 'PushManager' in window;
  const hasNotification = typeof window !== 'undefined' && 'Notification' in window;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  // iPadOS 13+ reports itself as a Mac, so touch points are the only tell.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone =
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true);

  let subscribed = false;
  if (hasServiceWorker && hasPush) {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      subscribed = Boolean(reg && (await reg.pushManager.getSubscription()));
    } catch {
      subscribed = false;
    }
  }
  return {
    hasServiceWorker,
    hasPush,
    hasNotification,
    permission: hasNotification ? Notification.permission : 'default',
    isIos,
    isStandalone,
    subscribed,
  };
}

/** Turn the test endpoint's answer into a sentence a person can act on. Telling
 * "never registered" from "registered but the push service refused" from "sent
 * and you did not hear it" is the entire reason the button exists. */
export function describeTest(result) {
  const { devices = 0, results = [] } = result || {};
  if (devices === 0) {
    return { tone: 'bad', text: 'No device is registered for you yet. Turn alerts on first, then test again.' };
  }
  const sent = results.filter((r) => r.status === 'sent').length;
  const gone = results.filter((r) => r.status === 'gone').length;
  const failed = results.filter((r) => r.status === 'failed').length;
  if (sent === devices) {
    return {
      tone: 'good',
      text: `Sent to ${sent === 1 ? '1 device' : `${sent} devices`}. If nothing buzzed, check this device is not on silent or in a Focus mode.`,
    };
  }
  if (sent === 0 && gone === devices) {
    return { tone: 'bad', text: 'That device is no longer registered. Turn alerts off and on again.' };
  }
  const bits = [];
  if (sent) bits.push(`${sent} sent`);
  if (gone) bits.push(`${gone} no longer registered`);
  if (failed) bits.push(`${failed} failed`);
  return { tone: 'warn', text: `Some did not go through: ${bits.join(', ')}.` };
}

/** base64url -> the Uint8Array `pushManager.subscribe` wants. */
export function urlBase64ToUint8Array(b64url) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** An API error as a sentence. The server's own message is plain English. */
async function failure(res) {
  let message = '';
  try {
    message = (await res.json())?.error?.message || '';
  } catch {
    /* not JSON */
  }
  if (res.status === 403 && !message) message = 'Only IT staff can turn urgent alerts on.';
  const err = new Error(message || `Something went wrong (${res.status}).`);
  err.status = res.status;
  return err;
}

/** Called from a TAP. Asks for permission first - see the header. */
export async function enableAlerts() {
  // Synchronous, so it adds no await before the permission prompt below. A
  // walkthrough build must not even ask for permission it can never use.
  assertLiveWritesAllowed();
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications were not allowed. You can change that in your device settings.');
  }

  const keyRes = await fetch('/api/push/key', { headers: { Accept: 'application/json' } });
  if (!keyRes.ok) throw await failure(keyRes);
  const { publicKey } = await keyRes.json();

  // register() is idempotent, and in production main.jsx has already done it;
  // doing it here as well means this also works on the dev server.
  await navigator.serviceWorker.register('/sw.js');
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true, // required by Safari, and the only mode Chrome offers
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const res = await guardedFetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...CSRF },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  if (!res.ok) throw await failure(res);
}

export async function disableAlerts() {
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return;
  // Tell the server first: if that fails we still hold the subscription and the
  // person can try again, rather than leaving a server row nothing can remove.
  const res = await guardedFetch('/api/push/subscribe', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...CSRF },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  if (!res.ok) throw await failure(res);
  await sub.unsubscribe();
}

export async function sendTestAlert() {
  const res = await guardedFetch('/api/push/test', { method: 'POST', headers: { ...CSRF } });
  if (!res.ok) throw await failure(res);
  return res.json();
}
